import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

// PokeClass button system — consistent rounded corners, tactile hover states,
// vibrant palette anchored on the app tokens (primary/secondary/destructive).
// Every variant shares the same interaction language so the whole panel feels
// coherent regardless of which screen it lives on.
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap select-none",
    "rounded-xl font-outfit font-bold text-sm",
    // Motion — subtle lift + press feedback, applied consistently.
    "transition-all duration-200 ease-out will-change-transform",
    "hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]",
    // Focus — thicker, brand-colored ring so keyboard users see it clearly.
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    // Disabled — no motion, muted look.
    "disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none disabled:translate-y-0",
    // Icon sizing default.
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        // Solid brand-primary — most-used call to action.
        default:
          "bg-primary text-primary-foreground shadow-md shadow-primary/25 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30",
        // Destructive — clear "danger" affordance for delete/remove.
        destructive:
          "bg-destructive text-destructive-foreground shadow-md shadow-red-500/25 hover:bg-destructive/90 hover:shadow-lg hover:shadow-red-500/30",
        // Outline — subtle bordered button, gains soft tint on hover.
        outline:
          "border-2 border-slate-200 bg-white text-slate-700 shadow-sm hover:border-primary/40 hover:bg-primary/5 hover:text-primary hover:shadow-md",
        // Secondary — supporting action; keeps a soft, warm feel.
        secondary:
          "bg-secondary text-secondary-foreground shadow-md shadow-amber-500/20 hover:bg-secondary/90 hover:shadow-lg hover:shadow-amber-500/25",
        // Ghost — tertiary action, no chrome until hover.
        ghost:
          "text-slate-700 hover:bg-slate-100 hover:text-slate-900",
        // Link — inline navigation, no chrome, no motion.
        link:
          "text-primary underline-offset-4 hover:underline hover:-translate-y-0 active:scale-100",
        // NEW — success (positive confirmations, save/apply).
        success:
          "bg-emerald-500 text-white shadow-md shadow-emerald-500/25 hover:bg-emerald-600 hover:shadow-lg hover:shadow-emerald-500/30",
        // NEW — warning (require attention actions).
        warning:
          "bg-amber-500 text-white shadow-md shadow-amber-500/25 hover:bg-amber-600 hover:shadow-lg hover:shadow-amber-500/30",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm:      "h-9  px-3.5 text-xs rounded-lg",
        lg:      "h-11 px-7 text-base",
        xl:      "h-12 px-8 text-base rounded-2xl",
        icon:    "h-10 w-10 rounded-xl",
        "icon-sm": "h-8 w-8 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
